import { DocumentRenderData, RequiredField } from '../types'
import {
  MergeField,
  CollapsibleClause,
  SignatureBlock,
  IntermediaryBlock,
  blank,
  formatDate,
} from './shared'

export const noticeOfAssignmentSellerRequiredFields: RequiredField[] = [
  { key: 'exchanger.names', description: 'Exchanger full name', sourceHub: 'purchase', sourceStep: 'Exchanger Info' },
  { key: 'seller.names', description: 'Seller full name', sourceHub: 'purchase', sourceStep: 'Seller Info' },
  { key: 'property.addressOrLegalDescription', description: 'Replacement property address', sourceHub: 'purchase', sourceStep: 'Replacement Property' },
]

interface Props {
  data: DocumentRenderData
}

export function NoticeOfAssignmentSellerTemplate({ data }: Props) {
  const { exchanger, seller, property, signature, bundleMeta } = data
  const exchangerLegal = exchanger.legalEntityDescription || exchanger.names
  const sellerName = seller?.names
  const sellerSigner = seller?.acknowledgmentSigner
  const noticeDate = formatDate(bundleMeta?.exchangeAgreementDate)
    || (signature?.capturedAt ? new Date(signature.capturedAt).toLocaleDateString() : '')
    || new Date().toLocaleDateString()

  return (
    <div className="space-y-6 text-sm text-foreground/85 font-serif">
      <div className="text-center">
        <h4 className="text-xl font-bold tracking-wide text-foreground">NOTICE TO SELLER</h4>
        <p className="text-xs text-muted-foreground/80 mt-1 italic">
          Phase Two — Closing Bundle (1031 Exchange)
        </p>
      </div>

      <div className="rounded-md border border-border bg-card/50 p-3 space-y-1 text-xs">
        <MergeField label="Date:" value={noticeDate} width={20} />
        <MergeField label="To: Escrow Officer" value={bundleMeta?.settlementAgent} width={32} />
        <MergeField label="File no.:" value={bundleMeta?.escrowFileNumber} width={28} />
        <MergeField label="Open 1031, Inc. File:" value={bundleMeta?.open1031FileNumber} width={28} />
        <MergeField label="Exchange Officer:" value={bundleMeta?.exchangeOfficer ?? 'Tom Bottenberg'} width={32} />
        <MergeField label="RE:" value={property?.addressOrLegalDescription} width={50} />
      </div>

      <section className="space-y-2">
        <h5 className="font-semibold text-sm">1031 EXCHANGE</h5>
        <p className="text-xs leading-relaxed">
          Seller acknowledges that Purchaser may engage in a tax-deferred exchange (the &ldquo;Exchange&rdquo;)
          pursuant to Section 1031 of the Internal Revenue Code of 1986, as amended, with respect to
          Existing Owner&rsquo;s disposition of the applicable Property.
        </p>
        <p className="text-xs leading-relaxed">
          As an accommodation to Purchaser, Seller agrees to cooperate with Purchaser in connection
          with the Exchange, and hereby consents to the assignment of this Agreement to the
          qualified intermediary, but only on the condition that:
        </p>
        <ul className="list-[lower-roman] list-inside text-xs space-y-1 ml-3">
          <li>the Exchange shall not delay Closing,</li>
          <li>
            the consummation or accomplishment of the Exchange shall not be a condition precedent or
            condition subsequent to Purchaser&rsquo;s obligations under this Agreement,
          </li>
          <li>
            Seller shall not be required to incur any obligations or liabilities in connection with
            the Exchange,
          </li>
          <li>
            Purchaser shall not be released of its obligations under this Agreement as a result of
            the Exchange, and
          </li>
          <li>
            Purchaser shall reimburse Seller for Seller&rsquo;s reasonable costs and expenses, if
            any, incurred in connection with the Exchange.
          </li>
        </ul>
        <p className="text-xs leading-relaxed">
          Seller shall have no obligation to execute any documents or to undertake any action by
          which Seller would or might incur any liability or obligation not otherwise provided for in
          the other provisions of this Agreement.
        </p>
        <p className="text-xs leading-relaxed">
          Purchaser shall indemnify and defend Seller and hold Seller harmless from and against any
          and all claims, damages, liabilities, losses, costs and expenses, including, without
          limitation, attorneys&rsquo; fees and costs, arising out of or in any way connected with
          the Exchange.
        </p>

        <CollapsibleClause title="View full closing instructions (Phase Two)">
          <p>
            <strong>1.</strong> For the above-referenced closing, please find the Notice to the
            Seller to be signed by the Seller and/or Closing Agent.
          </p>
          <p>
            <strong>2.</strong> Please return the Executed Documents via email to your Exchange
            Officer / Qualified Intermediary. Open 1031, Inc. must have the signed Exchange
            Documents (assignment of replacement property, authorization to disburse funds, wire
            instructions from the Closing Agent, Settlement Statement from the Closing Agent) 48
            hours in advance to wire for the closing.
          </p>
          <p>
            <strong>3.</strong> Settlement Statement 48 hours prior to closing — please provide an
            estimated settlement statement to Open 1031, Inc. for review. The Buyer
            (&ldquo;Exchanger&rdquo;) should approve and sign the settlement statement. The
            settlement statement should list the buyer as the Exchanger and may reference Open 1031,
            Inc. who is acting as a Qualified Intermediary under Exchange file number{' '}
            <span className="font-mono">{blank(bundleMeta?.open1031FileNumber, 18)}</span>.
          </p>
          <p>
            <strong>4.</strong> Direct Deed / Transfer of Title — The deed or other conveyance
            document should run directly between the Seller and the Exchanger. The Qualified
            Intermediary will NOT take title to the Replacement Property.
          </p>
          <p className="text-[11px] italic">
            If you have any questions regarding these instructions, please contact{' '}
            {bundleMeta?.exchangeOfficer ?? 'Tom Bottenberg'} at (866) 550-1031.
          </p>
        </CollapsibleClause>
      </section>

      <section className="border-t-2 border-border pt-4 space-y-4">
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-2">Seller Acknowledgment:</p>
          <p className="text-xs">{blank(sellerName, 50)}</p>
          <p className="text-xs mt-1">By: <span className="font-mono">{blank(sellerSigner, 30)}</span></p>
          <p className="text-xs">Date: <span className="font-mono">{blank(undefined, 20)}</span></p>
        </div>

        <SignatureBlock
          label="QUALIFIED INTERMEDIARY ACKNOWLEDGES DELIVERY:"
          signerName={`Qualified Intermediary on behalf of ${exchangerLegal}`}
          signature={signature}
        />

        <div className="pt-2">
          <IntermediaryBlock data={data} />
        </div>
      </section>
    </div>
  )
}
